import { BlueprintV2 } from '@editor/interfaces/blueprint'

import { ERROR_PROCESS } from './ErrorHandler'
import { GET_INSPECTION_AREA_API_URL } from './InspectionArea'
import { withStandardHandlers } from './Util'

export const GET_BLUEPRINTS_V2_API_URL = (projectId: string, areaId: string, blueprintId?: string) =>
  `${GET_INSPECTION_AREA_API_URL(projectId, areaId)}/blueprints-v2${blueprintId ? `/${blueprintId}` : ''}`

/**
 * Create a new blueprint in the inspection area.
 *
 * @param accessToken Auth0 access token
 * @param projectId Project ID
 * @param areaId Area ID
 * @param blueprint Blueprint data
 */
export const postBlueprint = (() => {
  const fn = (
    accessToken: string,
    projectId: string,
    areaId: string,
    { name, camera_profile, masking_region, inspection_items, polygons, cylinders, print_scale }: Partial<BlueprintV2>,
  ) =>
    fetch(`${GET_BLUEPRINTS_V2_API_URL(projectId, areaId)}`, {
      method: 'POST',
      headers: {
        responseType: 'json',
        'X-Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        camera_profile,
        masking_region,
        inspection_items,
        polygons,
        cylinders,
        print_scale,
      }),
    })

  return withStandardHandlers<typeof fn, BlueprintV2>(fn, ERROR_PROCESS.ADD_BLUEPRINT)
})()

/**
 * Fetch blueprints for a specific project and area.
 *
 * @param accessToken Auth0 access token
 * @param projectId Project ID
 * @param areaId Area ID
 */
export const getBlueprints = async (
  accessToken: string,
  projectId: string,
  areaId: string,
): Promise<{ results: BlueprintV2[] }> => {
  const doFetch = async (
    allBlueprints: BlueprintV2[],
    lastEvaluatedKey?: Record<string, string>,
  ): Promise<BlueprintV2[]> => {
    const params = new URLSearchParams({ limit: '1000' })
    if (lastEvaluatedKey) {
      params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey))
    }

    const fn = () =>
      fetch(`${GET_BLUEPRINTS_V2_API_URL(projectId, areaId)}?${params.toString()}`, {
        method: 'GET',
        headers: {
          responseType: 'json',
          'X-Authorization': `Bearer ${accessToken}`,
        },
      })

    const response = await withStandardHandlers<
      typeof fn,
      { results: BlueprintV2[]; last_evaluated_key?: Record<string, string> }
    >(fn, ERROR_PROCESS.GET_BLUEPRINTS)()

    allBlueprints.push(...response.results)

    if (response.last_evaluated_key) {
      return doFetch(allBlueprints, response.last_evaluated_key)
    }

    return allBlueprints
  }

  const blueprints = await doFetch([])
  return { results: blueprints }
}
